import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { AuditLog } from '../types';
import './AuditTrailList.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface AuditTrailListProps {
  authToken: string;
}

const AKTION_FARBE: Record<string, string> = {
  CREATE:       'badge-green',
  UPDATE:       'badge-blue',
  DEACTIVATE:   'badge-orange',
  ACTIVATE:     'badge-teal',
  AGENT_AKTION: 'badge-purple',
};

const AKTION_LABEL: Record<string, string> = {
  CREATE:       'Erstellt',
  UPDATE:       'Geändert',
  DEACTIVATE:   'Deaktiviert',
  ACTIVATE:     'Aktiviert',
  AGENT_AKTION: 'Agent',
};

const TABELLEN = ['partner', 'artikel', 'benutzer', 'agent'];

function formatDatum(iso: string): string {
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function JsonDiff({ alteWerte, neueWerte }: { alteWerte: string | null; neueWerte: string | null }) {
  const parse = (s: string | null) => {
    if (!s) return null;
    try { return JSON.parse(s); } catch { return s; }
  };
  const alt = parse(alteWerte);
  const neu = parse(neueWerte);

  if (!alt && !neu) return null;

  const renderObj = (obj: any, vergleich?: any, label?: string) => {
    if (!obj) return null;
    if (typeof obj !== 'object') return <span>{String(obj)}</span>;
    return (
      <table className="diff-table">
        <tbody>
          {Object.entries(obj).map(([k, v]) => {
            const geaendert = vergleich && vergleich[k] !== undefined && vergleich[k] !== v;
            return (
              <tr key={k} className={geaendert ? 'diff-changed' : ''}>
                <td className="diff-key">{k}</td>
                <td className="diff-val">{v === null ? <em>–</em> : String(v)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="audit-detail">
      {alt && (
        <div className="audit-detail-block">
          <span className="detail-label">Vorher</span>
          {renderObj(alt, neu)}
        </div>
      )}
      {neu && (
        <div className="audit-detail-block">
          <span className="detail-label">Nachher</span>
          {renderObj(neu, alt)}
        </div>
      )}
    </div>
  );
}

const AuditTrailList: React.FC<AuditTrailListProps> = ({ authToken }) => {
  const [eintraege, setEintraege] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Filter
  const [filterTabelle, setFilterTabelle] = useState('');
  const [filterAktion, setFilterAktion] = useState('');
  const [filterBenutzer, setFilterBenutzer] = useState('');
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const headers = useMemo(() => ({ Authorization: `Bearer ${authToken}` }), [authToken]);

  const load = useCallback(async (reset: boolean = true) => {
    setLoading(true);
    setError('');
    const newOffset = reset ? 0 : offset;
    if (reset) setOffset(0);
    try {
      const params: Record<string, any> = { limit: LIMIT, offset: newOffset };
      if (filterTabelle) params.tabelle = filterTabelle;
      if (filterAktion) params.aktion = filterAktion;
      const res = await axios.get(`${API_URL}/api/audit-trail`, { headers, params });
      if (reset) {
        setEintraege(res.data);
      } else {
        setEintraege(prev => [...prev, ...res.data]);
        setOffset(newOffset + res.data.length);
      }
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Fehler beim Laden des Audit-Trails');
    } finally {
      setLoading(false);
    }
  }, [filterTabelle, filterAktion, offset, headers]);

  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTabelle, filterAktion]);

  const gefilterteEintraege = eintraege.filter(e =>
    !filterBenutzer || (e.benutzer_name || '').toLowerCase().includes(filterBenutzer.toLowerCase())
  );

  return (
    <div className="audit-trail-list">
      <div className="list-header">
        <h2>Audit-Trail</h2>
        <span className="audit-hint">Unveränderliches GoBD-Protokoll aller Datenänderungen</span>
      </div>

      <div className="filter-bar">
        <select
          value={filterTabelle}
          onChange={e => setFilterTabelle(e.target.value)}
          className="filter-select"
        >
          <option value="">Alle Tabellen</option>
          {TABELLEN.map(t => (
            <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
          ))}
        </select>

        <select
          value={filterAktion}
          onChange={e => setFilterAktion(e.target.value)}
          className="filter-select"
        >
          <option value="">Alle Aktionen</option>
          {Object.entries(AKTION_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Benutzer suchen..."
          value={filterBenutzer}
          onChange={e => setFilterBenutzer(e.target.value)}
          className="filter-input"
        />
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading && eintraege.length === 0 ? (
        <div className="loading-state">Lade Audit-Trail...</div>
      ) : gefilterteEintraege.length === 0 ? (
        <div className="empty-state">Keine Einträge gefunden.</div>
      ) : (
        <>
          <table className="audit-table">
            <thead>
              <tr>
                <th>Zeitstempel</th>
                <th>Benutzer</th>
                <th>Aktion</th>
                <th>Tabelle</th>
                <th>ID</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {gefilterteEintraege.map(e => (
                <React.Fragment key={e.audit_log_id}>
                  <tr
                    className={expandedId === e.audit_log_id ? 'row-expanded' : ''}
                    onClick={() => setExpandedId(expandedId === e.audit_log_id ? null : e.audit_log_id)}
                  >
                    <td className="col-timestamp">{formatDatum(e.erstellt_am)}</td>
                    <td>{e.benutzer_name || '–'}</td>
                    <td>
                      <span className={`badge ${AKTION_FARBE[e.aktion] || ''}`}>
                        {AKTION_LABEL[e.aktion] || e.aktion}
                      </span>
                    </td>
                    <td className="col-tabelle">{e.tabelle}</td>
                    <td className="col-id">{e.datensatz_id ?? '–'}</td>
                    <td>
                      {(e.alte_werte || e.neue_werte) && (
                        <button
                          className="btn-detail"
                          onClick={ev => {
                            ev.stopPropagation();
                            setExpandedId(expandedId === e.audit_log_id ? null : e.audit_log_id);
                          }}
                        >
                          {expandedId === e.audit_log_id ? '▲ Schließen' : '▼ Anzeigen'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedId === e.audit_log_id && (
                    <tr className="detail-row">
                      <td colSpan={6}>
                        <JsonDiff alteWerte={e.alte_werte} neueWerte={e.neue_werte} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {eintraege.length >= LIMIT && (
            <button
              className="btn-load-more"
              onClick={() => load(false)}
              disabled={loading}
            >
              {loading ? 'Lädt...' : 'Ältere Einträge laden'}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default AuditTrailList;
